import React, { useEffect, useRef, useState } from 'react';

import { Flex } from 'antd-v5';
import { useDispatch, useSelector } from 'react-redux';
import { NOT_FOUND, redirect } from 'redux-first-router';

import { featureFlags } from '@modules/featureFlags/utils/featureFlags';
import { routeParamsSel } from '@modules/location/duck/selectorsTyped';
import UnitInformationCard from '@modules/roster-lite/components/RosterLitePage/UnitInformationCard';
import NewRosterContainer from '@modules/roster-v2/components/NewRosterTable';
import { rosterV2GateSel } from '@modules/roster-v2/duck/selectors';
import { useOrgRoster } from '@modules/roster-v2/hooks/useOrgRoster';
import PageContainer from '@shared/components/Layouts/PageContainer';
import Spin from '@shared/components/Spin';
import { organizationGuidSel } from '@shared/duck/selectors/general.selectors';

function RosterV2Container({ children }: { children: React.ReactNode }) {
  const dispatch = useDispatch();
  const { ready, allowed } = useSelector(rosterV2GateSel);
  const enabled = featureFlags.getFlag('SBL_6888_ROSTER_V2');

  useEffect(() => {
    if (ready && (!enabled || !allowed)) {
      dispatch(redirect({ type: NOT_FOUND, payload: { code: 403 } }));
    }
  }, [ready, enabled, allowed, dispatch]);

  if (!ready) {
    return (
      <PageContainer>
        <Spin />
      </PageContainer>
    );
  }

  if (!enabled || !allowed) return null;

  return <>{children}</>;
}

interface RosterV2RouteParams {
  organizationGuid?: string;
}

const RosterV2PageContents: React.FC = () => {
  const { organizationGuid: routeOrgGuid } = useSelector(
    routeParamsSel<RosterV2RouteParams>,
  );
  const selectedOrgGuid = useSelector(organizationGuidSel);

  // Start from the URL org at mount; follow the unit selector when it changes post-mount.
  const [orgGuid, setOrgGuid] = useState(() => routeOrgGuid ?? selectedOrgGuid);
  const hasMounted = useRef(false);
  useEffect(() => {
    if (!hasMounted.current) {
      hasMounted.current = true;
      return;
    }
    if (selectedOrgGuid) setOrgGuid(selectedOrgGuid);
  }, [selectedOrgGuid]);

  const { members, isFetching, subUnitLoading } = useOrgRoster(orgGuid);

  return (
    <PageContainer>
      {/* TODO(SBL-6888): reusing roster-lite's card; extract to a shared component. */}
      <Flex vertical gap={10}>
        <UnitInformationCard organizationGuid={orgGuid} />
        <NewRosterContainer
          orgGuid={orgGuid}
          members={members}
          loading={isFetching}
          subUnitLoading={subUnitLoading}
        />
      </Flex>
    </PageContainer>
  );
};

export default function RosterV2Page() {
  return (
    <RosterV2Container>
      <RosterV2PageContents />
    </RosterV2Container>
  );
}
